/**
 * Destructive confirmation dialog for disconnecting a user's Unipile (LinkedIn) account.
 *
 * Where: `Profile` and `UserDetailView` before calling disconnect.
 * Uses: MUI `Dialog`; local state for typed confirmation (`CONFIRM`).
 * Behavior: blocks close while `isDisconnecting`; clears input on close; calls `onConfirm` only when text matches.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Box,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

interface DisconnectUnipileConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDisconnecting?: boolean;
  userName?: string;
}

export const DisconnectUnipileConfirmModal: React.FC<DisconnectUnipileConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
  isDisconnecting = false,
  userName,
}) => {
  const [confirmText, setConfirmText] = useState('');
  const CONFIRM_TEXT = 'CONFIRM';

  const handleConfirm = () => {
    if (confirmText === CONFIRM_TEXT) {
      onConfirm();
      setConfirmText('');
    }
  };

  const handleClose = () => {
    if (!isDisconnecting) {
      setConfirmText('');
      onClose();
    }
  };

  const isConfirmValid = confirmText === CONFIRM_TEXT;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Disconnect Unipile Account
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Alert severity="error" icon={<WarningIcon />}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              This action is irreversible and cannot be undone.
            </Typography>
            <Typography variant="body2">
              All functionalities related to this Unipile account will be stopped. You will need to reconnect the account again if you want to restore these functionalities.
            </Typography>
          </Alert>

          {userName && (
            <Typography variant="body2" color="text.secondary">
              You are about to disconnect the Unipile account for <strong>{userName}</strong>.
            </Typography>
          )}

          <Typography variant="body2" color="text.secondary">
            To confirm this action, please type <strong>{CONFIRM_TEXT}</strong> in the field below:
          </Typography>

          <TextField
            fullWidth
            label={`Type ${CONFIRM_TEXT} to confirm`}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={isDisconnecting}
            error={confirmText !== '' && !isConfirmValid}
            helperText={
              confirmText !== '' && !isConfirmValid
                ? `You must type exactly "${CONFIRM_TEXT}"`
                : ''
            }
            onKeyPress={(e) => {
              if (e.key === 'Enter' && isConfirmValid && !isDisconnecting) {
                handleConfirm();
              }
            }}
            autoFocus
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isDisconnecting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={!isConfirmValid || isDisconnecting}
        >
          {isDisconnecting ? 'Disconnecting...' : 'Disconnect Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
